/**
 * Space Manage → Invites history: unified statuses, sort keys, and page sizes
 * shared by the list API and the admin page.
 *
 * Direct invites and invite links keep different stored statuses. The history
 * list uses one chip set; statuses that do not apply to a kind hide that kind
 * (with a banner) rather than silently mapping names.
 *
 * @module constants/space-invite-history
 */
export const SPACE_INVITE_HISTORY_STATUSES = [
    'pending',
    'accepted',
    'declined',
    'expired',
    'revoked',
    'active',
    'exhausted',
];
export const SPACE_INVITE_HISTORY_KINDS = ['direct', 'link'];
/** Direct-invite chips. `expired` also matches still-pending rows past `expiresAt`. */
export const SPACE_INVITE_HISTORY_DIRECT_STATUSES = [
    'pending',
    'accepted',
    'declined',
    'expired',
    'revoked',
];
/**
 * Invite-link chips. `exhausted` is virtual (still `active` in Mongo when the
 * use cap is hit). `expired` also matches still-active rows past `expiresAt`.
 */
export const SPACE_INVITE_HISTORY_LINK_STATUSES = [
    'active',
    'exhausted',
    'expired',
    'revoked',
];
export const SPACE_INVITE_HISTORY_SORTS = ['createdAt', 'expiresAt', 'useCount'];
export const SPACE_INVITE_HISTORY_SORT_DIRS = ['asc', 'desc'];
/** Default / max rows for one history page. */
export const SPACE_INVITE_HISTORY_PAGE_SIZE = 20;
export const SPACE_INVITE_HISTORY_PAGE_SIZE_MAX = 50;
/**
 * Safety cap on sort-key scans so a huge Space cannot unbounded-scan.
 * Larger than the recipient inbox cap because this is a Space-wide history.
 */
export const SPACE_INVITE_HISTORY_SCAN_CAP = 1000;
/** Nested joiner list under an expanded invite link. */
export const SPACE_INVITE_LINK_JOINERS_PAGE_SIZE = 10;
/** Minimum `q` length before username / displayName search runs. */
export const SPACE_INVITE_HISTORY_NAME_QUERY_MIN = 2;
export function spaceInviteHistoryStatusAppliesToKind(status, kind) {
    if (kind === 'direct') {
        return SPACE_INVITE_HISTORY_DIRECT_STATUSES.includes(status);
    }
    return SPACE_INVITE_HISTORY_LINK_STATUSES.includes(status);
}
/**
 * Which invite kinds disappear entirely for a status filter.
 *
 * An empty `statuses` array means "all" — nothing is hidden. Mixed filters
 * such as `accepted,expired` still show expired links, so `link` is not hidden.
 */
export function spaceInviteHistoryHiddenKinds(statuses) {
    if (statuses.length === 0)
        return [];
    const selected = new Set(statuses);
    const hidden = [];
    if (!SPACE_INVITE_HISTORY_DIRECT_STATUSES.some((status) => selected.has(status))) {
        hidden.push('direct');
    }
    if (!SPACE_INVITE_HISTORY_LINK_STATUSES.some((status) => selected.has(status))) {
        hidden.push('link');
    }
    return hidden;
}
/**
 * Which invite kinds the eye-menu filter wants. An empty `kinds` array means
 * "both" — the same convention as an empty status filter meaning "all".
 */
export function spaceInviteHistoryWantedKinds(kinds) {
    if (kinds.length === 0)
        return [...SPACE_INVITE_HISTORY_KINDS];
    return SPACE_INVITE_HISTORY_KINDS.filter((kind) => kinds.includes(kind));
}
//# sourceMappingURL=space-invite-history.js.map